import { useCallback, useEffect, useState } from "react";
import { FaEdit, FaLink, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import SystemRequirementFormModal from "../components/SystemRequirementFormModal";
import SystemRequirementModal from "../components/SystemRequirementModal";
import SystemRequirementSwLinksModal from "../components/SystemRequirementSwLinksModal";

const categoryOptions = [
  { value: "all", label: "All categories" },
  { value: "SW", label: "SW" },
  { value: "SWext", label: "SWext" },
  { value: "HW", label: "HW" },
  { value: "mechanical", label: "Mechanical" },
  { value: "definition", label: "Definition" },
  { value: "informative", label: "Informative" },
  { value: "others", label: "Others" }
];

const silFilterOptions = [
  { value: "all", label: "All SIL" },
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "none", label: "none" }
];

const softwareLinkFilterOptions = [
  { value: "all", label: "All links" },
  { value: "linked", label: "Linked" },
  { value: "unlinked", label: "Unlinked" }
];

const referenceFilterOptions = [
  { value: "all", label: "All references" },
  { value: "DMI", label: "ETCS DMI" },
  { value: "ss_121", label: "Subset-121" },
  { value: "ss_092_1", label: "Subset-092-1" },
  { value: "ss_104", label: "Subset-104" },
  { value: "ss_026", label: "Subset-026" },
  { value: "ss_035", label: "Subset-035" },
  { value: "INT", label: "Internal" }
];

const categoryColors = {
  SW: "#0d6efd",
  SWext: "#6610f2",
  HW: "#fd7e14",
  mechanical: "#6f42c1",
  definition: "#ffc107",
  informative: "#0dcaf0",
  others: "#6c757d"
};

const softwareLinkColors = {
  linked: "#198754",
  unlinked: "#adb5bd"
};

export default function SystemRequirements({ user }) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [linkingRequirement, setLinkingRequirement] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [silFilter, setSilFilter] = useState("all");
  const [softwareLinkFilter, setSoftwareLinkFilter] = useState("all");
  const [referenceFilter, setReferenceFilter] = useState("all");
  const [hideHeadings, setHideHeadings] = useState(false);
  const [metricsView, setMetricsView] = useState("donut");
  const [sortField, setSortField] = useState("number");
  const [sortDir, setSortDir] = useState("asc");

  const roleCodes = user?.roles ?? [];
  const canManage = roleCodes.includes("PM") || roleCodes.includes("RQM");

  const loadRequirements = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      if (silFilter !== "all") params.set("sil", silFilter);
      if (softwareLinkFilter !== "all") params.set("softwareLink", softwareLinkFilter);
      if (referenceFilter !== "all") params.set("reference", referenceFilter);
      if (hideHeadings) params.set("hideHeadings", "true");

      setLoading(true);
      const res = await fetchWithAuth(`/api/system-requirements?${params.toString()}`);

      if (!res.ok) {
        console.error("Failed to load system requirements");
        return;
      }

      setRequirements(await res.json());
    } catch (err) {
      console.warn("loadSystemRequirements aborted:", err.message);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, debouncedSearch, hideHeadings, referenceFilter, silFilter, softwareLinkFilter]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const openRequirement = async (id) => {
    const res = await fetchWithAuth(`/api/system-requirements/${id}`);

    if (!res.ok) return;

    setSelectedRequirement(await res.json());
    setShowModal(true);
  };

  const deleteRequirement = async (id) => {
    if (!window.confirm("Delete this system requirement?")) return;

    const res = await fetchWithAuth(`/api/system-requirements/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Delete failed");
      return;
    }

    loadRequirements();
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((current) => current === "asc" ? "desc" : "asc");
      return;
    }

    setSortField(field);
    setSortDir("asc");
  };

  const sortedRequirements = [...requirements].sort((leftRequirement, rightRequirement) => {
    const direction = sortDir === "asc" ? 1 : -1;
    const valueA = leftRequirement[sortField] ?? "";
    const valueB = rightRequirement[sortField] ?? "";

    const compare = String(valueA).localeCompare(String(valueB), undefined, {
      numeric: true,
      sensitivity: "base"
    });

    if (compare !== 0) return compare * direction;

    return String(leftRequirement.number ?? "").localeCompare(
      String(rightRequirement.number ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const renderLinkedSwRequirements = (requirement) => {
    const linked = requirement.SWRequirements ?? [];

    if (!linked.length) return <span className="text-muted">-</span>;

    return (
      <div className="d-flex flex-column gap-1">
        <span className="badge text-bg-light border align-self-start">
          {linked.length}
        </span>
        <span className="small text-muted">
          {linked.slice(0, 3).map((item) => item.number).join(", ")}
          {linked.length > 3 ? ", ..." : ""}
        </span>
      </div>
    );
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const hasSwRequirementLinks = (requirement) => (requirement.SWRequirements ?? []).length > 0;
  const isSection = (requirement) => requirement.nodeType === "section";
  const totalRequirements = requirements.length;
  const swRequirementsForLinkMetrics = requirements.filter((requirement) => requirement.category === "SW");
  const totalSwRequirementsForLinkMetrics = swRequirementsForLinkMetrics.length;

  const buildSegments = (items, total) => items
    .filter((item) => item.count > 0)
    .map((item) => ({
      ...item,
      percent: total > 0 ? (item.count / total) * 100 : 0
    }));

  const categorySegments = buildSegments(
    categoryOptions
      .filter((option) => option.value !== "all")
      .map((option) => ({
        key: option.value,
        label: option.label,
        count: requirements.filter((requirement) => requirement.category === option.value).length,
        color: categoryColors[option.value] ?? "#6c757d"
      })),
    totalRequirements
  );

  const softwareLinkSegments = buildSegments([
    {
      key: "linked",
      label: "Linked",
      count: swRequirementsForLinkMetrics.filter(hasSwRequirementLinks).length,
      color: softwareLinkColors.linked
    },
    {
      key: "unlinked",
      label: "Unlinked",
      count: swRequirementsForLinkMetrics.filter((requirement) => !hasSwRequirementLinks(requirement)).length,
      color: softwareLinkColors.unlinked
    }
  ], totalSwRequirementsForLinkMetrics);

  const buildDonutBackground = (segments) => {
    if (!segments.length) return "#e9ecef";

    let offset = 0;
    const stops = segments.map((segment) => {
      const start = offset;
      offset += segment.percent;
      return `${segment.color} ${start}% ${offset}%`;
    });

    return `conic-gradient(${stops.join(", ")})`;
  };

  const renderMetricBar = (segments, onSegmentClick) => (
    <>
      <div
        className="d-flex"
        style={{
          height: "14px",
          borderRadius: "999px",
          overflow: "hidden",
          backgroundColor: "#e9ecef"
        }}
      >
        {segments.map((segment) => (
          <button
            key={segment.key}
            type="button"
            title={`${segment.label}: ${segment.count} (${Math.round(segment.percent)}%)`}
            onClick={() => onSegmentClick(segment.key)}
            style={{
              width: `${segment.percent}%`,
              minWidth: segment.percent > 0 ? "6px" : "0",
              border: 0,
              padding: 0,
              backgroundColor: segment.color
            }}
          />
        ))}
      </div>
      {renderMetricLegend(segments, onSegmentClick)}
    </>
  );

  const renderMetricDonut = (segments, total, centerLabel, onSegmentClick) => (
    <div className="d-flex align-items-center gap-3">
      <div className="position-relative" style={{ width: "104px", height: "104px" }}>
        <div
          style={{
            width: "104px",
            height: "104px",
            borderRadius: "50%",
            background: buildDonutBackground(segments),
            boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.06)"
          }}
        />
        <div
          className="position-absolute top-50 start-50 translate-middle rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
          style={{
            width: "60px",
            height: "60px",
            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.04)"
          }}
        >
          <div className="fw-semibold" style={{ fontSize: "1rem", lineHeight: 1 }}>
            {total}
          </div>
          <div className="text-muted" style={{ fontSize: "0.66rem", lineHeight: 1.1 }}>
            {centerLabel}
          </div>
        </div>
      </div>
      {renderMetricLegend(segments, onSegmentClick)}
    </div>
  );

  const renderMetricLegend = (segments, onSegmentClick) => (
    <div className="d-flex flex-column gap-2 small">
      {segments.map((segment) => (
        <button
          key={segment.key}
          type="button"
          className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-2 text-start"
          onClick={() => onSegmentClick(segment.key)}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "999px",
              display: "inline-block",
              backgroundColor: segment.color
            }}
          />
          <span>
            {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
          </span>
        </button>
      ))}
    </div>
  );

  const getRowStyle = (index, requirement) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff",
    ...(hasSwRequirementLinks(requirement) && !isSection(requirement) ? {
      borderLeft: "4px solid rgba(25, 135, 84, 0.85)"
    } : {})
  });

  const sectionRowStyle = {
    "--bs-table-bg": "rgba(13, 110, 253, 0.12)",
    borderTop: "2px solid rgba(13, 110, 253, 0.24)"
  };

  const systemRequirementColumnCount = canManage ? 7 : 6;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search system requirements..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                }}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <select
            className="form-select"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            style={{ width: "180px" }}
            aria-label="Filter by category"
          >
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="form-select"
            value={silFilter}
            onChange={(event) => setSilFilter(event.target.value)}
            style={{ width: "120px" }}
            aria-label="Filter by SIL"
          >
            {silFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="form-select"
            value={softwareLinkFilter}
            onChange={(event) => setSoftwareLinkFilter(event.target.value)}
            style={{ width: "150px" }}
            aria-label="Filter by software links"
          >
            {softwareLinkFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="form-select"
            value={referenceFilter}
            onChange={(event) => setReferenceFilter(event.target.value)}
            style={{ width: "170px" }}
            aria-label="Filter by reference"
          >
            {referenceFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="form-check form-switch m-0">
            <input
              id="system-requirements-hide-headings"
              type="checkbox"
              className="form-check-input"
              checked={hideHeadings}
              onChange={(event) => setHideHeadings(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="system-requirements-hide-headings">
              Hide headings
            </label>
          </div>
        </div>

        {canManage && (
          <button
            className="btn btn-success"
            onClick={() => {
              setEditingRequirement(null);
              setShowForm(true);
            }}
          >
            Add system requirement
          </button>
        )}
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-2">
          <div className="small text-muted">
            Distribution for {totalRequirements} system requirement{totalRequirements === 1 ? "" : "s"}
          </div>
          <div className="btn-group btn-group-sm" role="group" aria-label="Metrics view">
            <button
              type="button"
              className={`btn ${metricsView === "bar" ? "btn-secondary" : "btn-outline-secondary"}`}
              onClick={() => setMetricsView("bar")}
            >
              Bar
            </button>
            <button
              type="button"
              className={`btn ${metricsView === "donut" ? "btn-secondary" : "btn-outline-secondary"}`}
              onClick={() => setMetricsView("donut")}
            >
              Donut
            </button>
          </div>
        </div>

        {metricsView === "bar" ? (
          <div className="d-flex flex-column gap-3">
            {renderMetricBar(categorySegments, setCategoryFilter)}
            {renderMetricBar(softwareLinkSegments, setSoftwareLinkFilter)}
          </div>
        ) : (
          <div className="d-flex align-items-start gap-5 flex-wrap">
            {renderMetricDonut(categorySegments, totalRequirements, "category", setCategoryFilter)}
            {renderMetricDonut(softwareLinkSegments, totalSwRequirementsForLinkMetrics, "SW links", setSoftwareLinkFilter)}
          </div>
        )}
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th
                style={{ ...stickyHeaderStyle, width: "180px", cursor: "pointer" }}
                onClick={() => handleSort("number")}
              >
                <span className="d-inline-flex align-items-center">
                  Number
                  {renderSortIndicator("number")}
                </span>
              </th>
              <th
                style={{ ...stickyHeaderStyle, cursor: "pointer" }}
                onClick={() => handleSort("name")}
              >
                <span className="d-inline-flex align-items-center">
                  Name
                  {renderSortIndicator("name")}
                </span>
              </th>
              <th
                className="text-center"
                style={{ ...stickyHeaderStyle, width: "120px", cursor: "pointer" }}
                onClick={() => handleSort("category")}
              >
                <span className="d-inline-flex align-items-center">
                  Category
                  {renderSortIndicator("category")}
                </span>
              </th>
              <th
                className="text-center"
                style={{ ...stickyHeaderStyle, width: "90px", cursor: "pointer" }}
                onClick={() => handleSort("sil")}
              >
                <span className="d-inline-flex align-items-center">
                  SIL
                  {renderSortIndicator("sil")}
                </span>
              </th>
              <th style={{ ...stickyHeaderStyle, width: "180px" }}>SW Requirements</th>
              <th style={{ ...stickyHeaderStyle, minWidth: "260px" }}>Note</th>
              {canManage && <th style={{ ...stickyHeaderStyle, width: "150px" }}>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={canManage ? 7 : 6} className="text-center text-muted py-4">
                  Loading system requirements...
                </td>
              </tr>
            ) : sortedRequirements.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 7 : 6} className="text-center text-muted py-4">
                  No system requirements found.
                </td>
              </tr>
            ) : sortedRequirements.map((requirement, index) => (
              isSection(requirement) ? (
                <tr key={requirement.id} style={sectionRowStyle}>
                  <td colSpan={systemRequirementColumnCount} className="py-2">
                    <div className="d-flex align-items-baseline gap-3">
                      <span className="fw-bold text-primary">
                        {renderHighlightedText(requirement.number)}
                      </span>
                      <span className="fw-semibold">
                        {renderHighlightedText(requirement.name)}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
              <tr key={requirement.id} style={getRowStyle(index, requirement)}>
                <td>
                  <button
                    className="btn btn-link p-0 fw-bold text-decoration-none text-start"
                    onClick={() => openRequirement(requirement.id)}
                  >
                    {renderHighlightedText(requirement.number)}
                  </button>
                </td>
                <td>{renderHighlightedText(requirement.name)}</td>
                <td className="text-center">{requirement.category}</td>
                <td className="text-center">{requirement.sil ?? requirement.SIL ?? <span className="text-muted">-</span>}</td>
                <td>{renderLinkedSwRequirements(requirement)}</td>
                <td>{renderHighlightedText(requirement.note)}</td>
                {canManage && (
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => {
                        setEditingRequirement(requirement);
                        setShowForm(true);
                      }}
                    >
                      <FaEdit />
                    </button>

                    <button
                      className="btn btn-sm btn-outline-success me-2"
                      onClick={() => {
                        setLinkingRequirement(requirement);
                        setShowLinksModal(true);
                      }}
                      title="Link SW requirements"
                      aria-label="Link SW requirements"
                    >
                      <FaLink />
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteRequirement(requirement.id)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                )}
              </tr>
              )
            ))}
          </tbody>
        </table>
      </div>

      <SystemRequirementFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadRequirements}
        requirement={editingRequirement}
      />

      <SystemRequirementModal
        show={showModal}
        onClose={() => setShowModal(false)}
        requirement={selectedRequirement}
      />

      <SystemRequirementSwLinksModal
        show={showLinksModal}
        onClose={() => setShowLinksModal(false)}
        onSaved={loadRequirements}
        requirement={linkingRequirement}
      />
    </div>
  );
}
